// services/childcare/children.js
import { api, uploadApi } from "./utils";

/**
 * Get all children in the facility
 * @returns {Promise<Object>} Response with children data array
 */
export const getChildren = async () => {
  try {
    const response = await api.get("/childcare/children");
    return response;
  } catch (error) {
    console.error("Error fetching children:", error);
    throw error;
  }
};

/**
 * Get a single child by ID
 * @param {string|number} id - Child ID
 * @returns {Promise<Object>} Response with child data
 */
export const getChildById = async (id) => {
  try {
    const response = await api.get(`/childcare/children/${id}`);
    return response;
  } catch (error) {
    console.error("Error fetching child:", error);
    throw error;
  }
};

/**
 * Create a new child profile
 * @param {FormData} formData - Form data with all child information
 * @returns {Promise<Object>} Response with created child data
 */
export const createChild = async (formData) => {
  try {
    const response = await api.post("/childcare/children", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  } catch (error) {
    console.error("Create child error:", error);
    throw error;
  }
};

/**
 * Get full child details including parent address, medical fields, and emergency contacts.
 * Returns source: 'childcare' | 'parent' to drive conditional UI rendering.
 * @param {string|number} id - Child ID
 * @returns {Promise<Object>} Response with full child data
 */
export const getChildDetails = async (id) => {
  const response = await api.get(`/childcare/children/${id}/details`);
  return response;
};

/**
 * Update child — sends as multipart/form-data so photo files are supported.
 * @param {string|number} id - Child ID
 * @param {Object} data - Fields to update (photo as File, emergency_contacts as array)
 * @returns {Promise<Object>} Response with updated child data
 */
export const updateChild = async (id, data) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) continue;
    if (key === 'photo' && value instanceof File) {
      formData.append('photo', value);
    } else if (key === 'emergency_contacts' && Array.isArray(value)) {
      formData.append('emergency_contacts', JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  }
  const response = await uploadApi.put(`/childcare/children/${id}`, formData);
  return response;
};

/**
 * Update child field (for individual field updates)
 * @param {string|number} id - Child ID
 * @param {string} field - Field name to update
 * @param {any} value - New value for the field
 * @returns {Promise<Object>} Response with updated field
 */
export const updateChildField = async (id, field, value) => {
  const response = await api.patch(`/childcare/children/${id}/field`, {
    field,
    value,
  });
  return response;
};

/**
 * Delete child (soft delete)
 * @param {string|number} id - Child ID
 * @returns {Promise<Object>} Response with delete confirmation
 */
export const deleteChild = async (id) => {
  try {
    const response = await api.delete(`/childcare/children/${id}`);
    return response;
  } catch (error) {
    console.error("Error deleting child:", error);
    throw error;
  }
};

/**
 * Bulk delete children
 * @param {Array} childIds - Array of child IDs to delete
 * @returns {Promise<Object>} Response with bulk delete confirmation
 */
export const bulkDeleteChildren = async (childIds) => {
  try {
    // Backend expects 'ids', not 'childIds' — fixed for compressed schema
    const response = await api.post("/childcare/children/bulk-delete", {
      ids: childIds,
    });
    return response;
  } catch (error) {
    console.error("Error bulk deleting children:", error);
    throw error;
  }
};

/**
 * Get children by class
 * @param {string|number} classId - Class ID
 * @returns {Promise<Object>} Response with children data array
 */
export const getChildrenByClass = async (classId) => {
  try {
    const response = await api.get(`/childcare/children/class/${classId}`);
    return response;
  } catch (error) {
    console.error("Error fetching children by class:", error);
    throw error;
  }
};

/**
 * Get children by caregiver
 * @param {string|number} caregiverId - Caregiver ID
 * @returns {Promise<Object>} Response with children data array
 */
export const getChildrenByCaregiver = async (caregiverId) => {
  try {
    const response = await api.get(
      `/childcare/children/caregiver/${caregiverId}`,
    );
    return response;
  } catch (error) {
    console.error("Error fetching children by caregiver:", error);
    throw error;
  }
};

/**
 * Search children
 * @param {Object} searchParams - Search parameters
 * @returns {Promise<Object>} Response with search results
 */
export const searchChildren = async (searchParams) => {
  try {
    const response = await api.get("/childcare/children/search", {
      params: searchParams,
    });
    return response;
  } catch (error) {
    console.error("Error searching children:", error);
    throw error;
  }
};

/**
 * Export children data
 * @param {string} format - Export format (csv, excel, pdf)
 * @returns {Promise<Object>} Response with exported data
 */
export const exportChildren = async (format = "csv") => {
  try {
    const response = await api.get(
      `/childcare/children/export?format=${format}`,
    );
    return response;
  } catch (error) {
    console.error("Error exporting children:", error);
    throw error;
  }
};

/**
 * Get child statistics
 * @returns {Promise<Object>} Response with child statistics
 */
export const getChildStats = async () => {
  try {
    const response = await api.get("/childcare/children/stats");
    return response;
  } catch (error) {
    console.error("Error fetching child stats:", error);
    throw error;
  }
};
// Get children for the logged-in parent (using parent API)
export const getParentChildren = async () => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const response = await api.get("/childcare/parent/children", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

// Enroll child in facility
export const enrollChildInFacility = async (enrollmentData) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const response = await api.post("/childcare/parent/enroll", enrollmentData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

// Get parent's enrollments
export const getParentEnrollments = async () => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const response = await api.get("/childcare/parent/enrollments", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

// Check if child is enrolled in a program
export const checkEnrollment = async (childId, programId) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const response = await api.get(
    `/childcare/parent/enrollments/check/${childId}/${programId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return response;
};

// Cancel enrollment
export const cancelEnrollment = async (enrollmentId) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const response = await api.delete(
    `/childcare/parent/enrollments/${enrollmentId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return response;
};

// ─── Staff enrollment management ─────────────────────────────────────────────

export const getFacilityEnrollments = async (status) => {
  const params = status ? { status } : {};
  return await api.get("/childcare/enrollments", { params });
};

export const approveEnrollment = async (enrollmentId) => {
  return await api.put(`/childcare/enrollments/${enrollmentId}/approve`);
};

export const rejectEnrollment = async (enrollmentId) => {
  return await api.put(`/childcare/enrollments/${enrollmentId}/reject`);
};

/**
 * normalizeChild — maps compressed-schema response fields to the flat field
 * names the UI was written against. Safe to run on both old and new responses.
 */
export const normalizeChild = (c) => ({
  ...c,
  enrollment_status: c.enrollment_status ?? (c.is_active ? 'active' : 'withdrawn'),
  parent_name:  c.parent_name  ?? c.primary_parent?.name  ?? null,
  parent_email: c.parent_email ?? c.primary_parent?.email ?? null,
});
